import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  CubeIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  CheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import { api } from '../services/authService';

const AuxiliaryEquipmentPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    has_monthly_charge: false,
    monthly_rate: '',
    quantity_total: 1,
    quantity_available: 1,
    notes: ''
  });

  // Excel operations state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [exportFilters, setExportFilters] = useState({
    category: '',
    status: ''
  });
  const [bulkUpdateData, setBulkUpdateData] = useState({
    status: '',
    condition: ''
  });

  useEffect(() => {
    loadEquipment();
    loadCategories();
  }, []);

  const loadEquipment = async () => {
    try {
      setLoading(true);

      const response = await api.get('/api/auxiliary-equipment');
      const data = response.data;
      setEquipment(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
      toast.error('Erro ao carregar equipamentos');
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/auxiliary-equipment/categories');
      const data = response.data;
      setCategories(data.data?.flat() || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  // Category hierarchy helpers
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getParentCategories = () => {
    return categories
      .filter(cat => !cat.parent_id)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  const getChildCategories = (parentId) => {
    return categories
      .filter(cat => cat.parent_id === parentId)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingItem) {
        await api.put(`/api/auxiliary-equipment/${editingItem.id}`, formData);
      } else {
        await api.post('/api/auxiliary-equipment', formData);
      }

      toast.success(editingItem ? 'Equipamento atualizado!' : 'Equipamento criado!');
      loadEquipment();
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      const message = error.response?.data?.error || 'Erro ao salvar equipamento';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      monthly_rate: item.monthly_rate,
      quantity_total: item.quantity_total,
      quantity_available: item.quantity_available,
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Deseja realmente remover este equipamento?')) return;

    try {
      await api.delete(`/api/auxiliary-equipment/${id}`);
      toast.success('Equipamento removido!');
      loadEquipment();
    } catch (error) {
      console.error('Erro ao remover:', error);
      const message = error.response?.data?.error || 'Erro ao remover equipamento';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category_id: '',
      has_monthly_charge: false,
      monthly_rate: '',
      quantity_total: 1,
      quantity_available: 1,
      notes: ''
    });
  };

  // Excel operations handlers
  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/auxiliary-equipment/export', exportFilters, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `equipamentos_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Exportação concluída!');
      setShowExportModal(false);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo Excel');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post('/api/auxiliary-equipment/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`${response.data.imported} equipamentos importados!`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} erros. Verifique console.`);
        console.log('Erros:', response.data.errors);
      }

      setShowImportModal(false);
      setImportFile(null);
      loadEquipment();
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedItems.length === 0) {
      toast.error('Selecione pelo menos um equipamento');
      return;
    }

    if (!bulkUpdateData.status && !bulkUpdateData.condition) {
      toast.error('Preencha pelo menos um campo');
      return;
    }

    try {
      setLoading(true);
      const response = await api.patch('/api/auxiliary-equipment/bulk-update', {
        ids: selectedItems,
        updates: bulkUpdateData
      });

      toast.success(`${response.data.updated} equipamentos atualizados!`);
      setShowBulkUpdateModal(false);
      setBulkUpdateData({ status: '', condition: '' });
      setSelectedItems([]);
      loadEquipment();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast.error('Erro ao atualizar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const selectAllItems = () => {
    setSelectedItems(filteredEquipment.map(item => item.id));
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const getCategoryInfo = (category_id) => {
    const cat = categories.find(c => c.id === category_id);
    return cat || { id: null, name: 'Sem categoria', icon: '📦' };
  };

  const filteredEquipment = filterCategory === 'all'
    ? equipment
    : equipment.filter(item => item.category_id === filterCategory);

  const getStatusBadge = (status) => {
    const badges = {
      available: { text: 'Disponível', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      rented: { text: 'Alugado', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      maintenance: { text: 'Manutenção', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' }
    };
    return badges[status] || badges.available;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Equipamentos Auxiliares
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gerencie equipamentos que podem ser alugados junto com os veículos
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Excel Operations Buttons */}
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Exportar para Excel"
          >
            <DocumentArrowDownIcon className="w-5 h-5" />
            Exportar
          </button>

          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            title="Importar do Excel"
          >
            <DocumentArrowUpIcon className="w-5 h-5" />
            Importar
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Novo Equipamento
          </button>
        </div>
      </div>

      {/* Bulk Update Controls */}
      {selectedItems.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedItems.length} item(ns) selecionado(s)
              </span>
              <button
                onClick={selectAllItems}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Selecionar todos
              </button>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Limpar seleção
              </button>
            </div>
            <button
              onClick={() => setShowBulkUpdateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CheckIcon className="w-5 h-5" />
              Atualizar Selecionados
            </button>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filtrar por categoria:
        </label>
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 p-3 max-h-64 overflow-y-auto">
          {/* Opção "Todas" */}
          <div
            onClick={() => setFilterCategory('all')}
            className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-colors ${
              filterCategory === 'all'
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-100'
                : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
            }`}
          >
            <span className="font-medium">Todas as categorias</span>
          </div>

          {/* Categorias em árvore */}
          {getParentCategories().map(parent => {
            const children = getChildCategories(parent.id);
            const isExpanded = expandedCategories.has(parent.id);
            const hasChildren = children.length > 0;

            return (
              <div key={parent.id} className="mt-1">
                {/* Categoria Pai */}
                <div className="flex items-center gap-2">
                  {hasChildren && (
                    <button
                      onClick={() => toggleCategoryExpansion(parent.id)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  )}
                  {!hasChildren && <div className="w-6" />}
                  <div
                    onClick={() => setFilterCategory(parent.id)}
                    className={`flex-1 px-3 py-2 rounded cursor-pointer transition-colors ${
                      filterCategory === parent.id
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                    }`}
                  >
                    <span className="font-semibold text-base">{parent.name}</span>
                  </div>
                </div>

                {/* Categorias Filhas */}
                {hasChildren && isExpanded && (
                  <div className="ml-8 mt-1 space-y-1">
                    {children.map(child => (
                      <div
                        key={child.id}
                        onClick={() => setFilterCategory(child.id)}
                        className={`px-3 py-2 rounded cursor-pointer transition-colors ${
                          filterCategory === child.id
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-650 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="text-sm">{child.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {equipment.length}
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            Total de Equipamentos
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {equipment.filter(e => e.status === 'available').length}
          </div>
          <div className="text-sm text-green-700 dark:text-green-300">
            Disponíveis
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {equipment.reduce((sum, e) => sum + (e.quantity_total - e.quantity_available), 0)}
          </div>
          <div className="text-sm text-purple-700 dark:text-purple-300">
            Alugados
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {equipment.reduce((sum, e) => sum + e.quantity_available, 0)}
          </div>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            Unidades Disponíveis
          </div>
        </div>
      </div>

      {/* Lista de Equipamentos */}
      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEquipment.map((item) => {
            const categoryInfo = getCategoryInfo(item.category_id);
            const statusBadge = getStatusBadge(item.status);

            return (
              <div
                key={item.id}
                className="relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Selection Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.id)}
                  onChange={() => toggleItemSelection(item.id)}
                  className="absolute top-3 left-3 w-5 h-5 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />

                <div className="flex items-start justify-between mb-4 ml-8">
                  <div className="flex flex-col">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {item.name}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {categoryInfo.name}
                    </span>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${statusBadge.class}`}>
                    {statusBadge.text}
                  </span>
                </div>

                {item.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {item.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Valor Mensal:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(item.monthly_rate)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Disponível:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {item.quantity_available} / {item.quantity_total}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleEdit(item)}
                    className="flex-1 p-2 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded transition-colors"
                    title="Editar"
                  >
                    <PencilSquareIcon className="w-5 h-5 mx-auto" />
                  </button>

                  <button
                    onClick={() => handleDelete(item.id)}
                    className="flex-1 p-2 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded transition-colors"
                    title="Remover"
                  >
                    <TrashIcon className="w-5 h-5 mx-auto" />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredEquipment.length === 0 && (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum equipamento encontrado
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {editingItem ? 'Editar Equipamento' : 'Novo Equipamento'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Ex: Kit Ferramentas Básicas"
                    required
                  />
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={3}
                    placeholder="Descrição detalhada do equipamento"
                  />
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {getParentCategories().map(parent => {
                      const children = getChildCategories(parent.id);
                      return (
                        <React.Fragment key={parent.id}>
                          {/* Categoria Pai - em negrito através de caracteres especiais */}
                          <option value={parent.id} className="font-bold">
                            ▶ {parent.name}
                          </option>
                          {/* Categorias Filhas - com indentação */}
                          {children.map(child => (
                            <option key={child.id} value={child.id}>
                              &nbsp;&nbsp;&nbsp;&nbsp;└─ {child.name}
                            </option>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </select>
                </div>

                {/* Cobrança Mensal */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Valor Mensal (R$)
                  </label>
                  <div className="flex items-center gap-3">
                    {/* Toggle button para cobrança mensal */}
                    <label className="flex items-center cursor-pointer whitespace-nowrap shrink-0">
                      <input
                        type="checkbox"
                        checked={formData.has_monthly_charge}
                        onChange={(e) => setFormData({
                          ...formData,
                          has_monthly_charge: e.target.checked,
                          monthly_rate: e.target.checked ? formData.monthly_rate : ''
                        })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Cobrar adicional
                      </span>
                    </label>

                    {/* Campo de valor mensal */}
                    <input
                      type="number"
                      step="0.01"
                      value={formData.monthly_rate}
                      onChange={(e) => setFormData({ ...formData, monthly_rate: e.target.value })}
                      disabled={!formData.has_monthly_charge}
                      className={`flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${
                        formData.has_monthly_charge
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Quantidades */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantidade Total *
                    </label>
                    <input
                      type="number"
                      value={formData.quantity_total}
                      onChange={(e) => setFormData({ ...formData, quantity_total: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantidade Disponível *
                    </label>
                    <input
                      type="number"
                      value={formData.quantity_available}
                      onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="0"
                      max={formData.quantity_total}
                      required
                    />
                  </div>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                    placeholder="Observações internas"
                  />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    {editingItem ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowExportModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Exportar Equipamentos
              </h3>

              <div className="space-y-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria
                  </label>
                  <select
                    value={exportFilters.category}
                    onChange={(e) => setExportFilters({ ...exportFilters, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Todas as categorias</option>
                    {getParentCategories().map(parent => {
                      const children = getChildCategories(parent.id);
                      return (
                        <React.Fragment key={parent.id}>
                          {/* Categoria Pai */}
                          <option value={parent.id}>
                            ▶ {parent.name}
                          </option>
                          {/* Categorias Filhas */}
                          {children.map(child => (
                            <option key={child.id} value={child.id}>
                              &nbsp;&nbsp;&nbsp;&nbsp;└─ {child.name}
                            </option>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={exportFilters.status}
                    onChange={(e) => setExportFilters({ ...exportFilters, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Todos os status</option>
                    <option value="available">Disponível</option>
                    <option value="in_use">Em Uso</option>
                    <option value="maintenance">Em Manutenção</option>
                    <option value="retired">Aposentado</option>
                  </select>
                </div>

                {/* Info Text */}
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {filteredEquipment.length} equipamento(s) será(ão) exportado(s)
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <DocumentArrowDownIcon className="w-5 h-5" />
                  Exportar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImportModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Importar Equipamentos
              </h3>

              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Arquivo Excel (.xlsx)
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setImportFile(file);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                  />
                  {importFile && (
                    <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                      ✓ {importFile.name}
                    </p>
                  )}
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
                    Formato do arquivo:
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                    <li>• Nome</li>
                    <li>• Marca</li>
                    <li>• Modelo</li>
                    <li>• Número de Série</li>
                    <li>• Categoria ID</li>
                    <li>• Quantidade Disponível</li>
                    <li>• Preço de Compra</li>
                    <li>• Preço Diário</li>
                  </ul>
                </div>

                {/* Download Template */}
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  <DocumentArrowDownIcon className="w-5 h-5" />
                  Baixar Template
                </button>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={!importFile}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <DocumentArrowUpIcon className="w-5 h-5" />
                  Importar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkUpdateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
             onClick={() => setShowBulkUpdateModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Atualização em Massa
                </h3>
                <button
                  onClick={() => setShowBulkUpdateModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>{selectedItems.length}</strong> {selectedItems.length === 1 ? 'equipamento selecionado' : 'equipamentos selecionados'}.
                  Preencha apenas os campos que deseja atualizar.
                </p>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={bulkUpdateData.status || ''}
                    onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">-- Não alterar --</option>
                    <option value="available">Disponível</option>
                    <option value="in_use">Em Uso</option>
                    <option value="maintenance">Manutenção</option>
                    <option value="out_of_service">Fora de Serviço</option>
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria
                  </label>
                  <select
                    value={bulkUpdateData.category_id || ''}
                    onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">-- Não alterar --</option>
                    {getParentCategories().map(parent => {
                      const children = getChildCategories(parent.id);
                      return (
                        <React.Fragment key={parent.id}>
                          {/* Categoria Pai */}
                          <option value={parent.id}>
                            ▶ {parent.name}
                          </option>
                          {/* Categorias Filhas */}
                          {children.map(child => (
                            <option key={child.id} value={child.id}>
                              &nbsp;&nbsp;&nbsp;&nbsp;└─ {child.name}
                            </option>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </select>
                </div>

                {/* Prices Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Purchase Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor de Compra (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkUpdateData.purchase_price || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, purchase_price: e.target.value })}
                      placeholder="Não alterar"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Daily Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Diária (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkUpdateData.daily_rate || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, daily_rate: e.target.value })}
                      placeholder="Não alterar"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Weekly Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Semanal (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkUpdateData.weekly_rate || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, weekly_rate: e.target.value })}
                      placeholder="Não alterar"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Monthly Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mensal (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkUpdateData.monthly_rate || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, monthly_rate: e.target.value })}
                      placeholder="Não alterar"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBulkUpdateModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleBulkUpdate}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <CheckIcon className="w-5 h-5" />
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuxiliaryEquipmentPage;
